import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/i18n";
import { FULL_RODO } from "@/lib/rodo";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";

function Block({ title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white/80 dark:bg-slate-900/80 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-xl p-6">
      <h4 className="font-display font-bold text-base tracking-tight">{title}</h4>
      {children}
    </div>
  );
}

export default function Rodo() {
  const { t } = useT();
  useSeo({ title: "Polityka prywatności (RODO)", description: "Informacja RODO wirtualnego biura Twoje Biuro w Warszawie — administrator danych, cele przetwarzania, prawa osób.", path: ROUTES.rodo, breadcrumbs: [{ name: "RODO", path: ROUTES.rodo }] });
  const k = t.knowledge.rodo;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [t.nav.rodo]);

  return (
    <div data-testid="rodo-page" className="min-h-screen text-foreground">
      <Background />
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="rodo-logo-link" aria-label="Wirtualne biuro Warszawa — Twoje Biuro, strona główna">
            <Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} />
          </Link>
          <Link to="/" data-testid="rodo-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
            <ArrowLeft size={13} /> {t.footer.back_home}
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400 font-semibold">{t.nav.rodo}</p>
        <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4">{k.title}</h1>
        <p className="mt-5 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">{k.intro}</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Block title={k.admin_t} testid="rodo-block-administrator">
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2">{k.admin_p1}</p>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2">{k.admin_p2}</p>
          </Block>
          <Block title={k.goals_t} testid="rodo-block-cele">
            <ul className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2 space-y-2">
              {k.goals.map((g, i) => (
                <li key={i} className="flex gap-2"><span className="w-1 h-1 rounded-full bg-slate-400 mt-2 shrink-0" />{g}</li>
              ))}
            </ul>
          </Block>
          <Block title={k.rights_t} testid="rodo-block-uprawnienia">
            <ul className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2 space-y-2">
              {k.rights.map((r, i) => (
                <li key={i} className="flex gap-2"><span className="w-1 h-1 rounded-full bg-slate-400 mt-2 shrink-0" />{r}</li>
              ))}
            </ul>
          </Block>
          <Block title={k.period_t} testid="rodo-block-okres">
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2">{k.period_p}</p>
          </Block>
        </div>

        <div className="mt-6 space-y-6">
          <Block title={k.transfer_t} testid="rodo-block-transfer">
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2">{k.transfer_p1}</p>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-3">{k.transfer_p2}</p>
          </Block>
          <Block title={k.turnstile_t} testid="rodo-block-turnstile">
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mt-2">
              {k.turnstile_p.split("(https://www.cloudflare.com/turnstile-privacy-policy/)")[0]}
              (<a data-testid="rodo-turnstile-link" href="https://www.cloudflare.com/turnstile-privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground">cloudflare.com/turnstile-privacy-policy</a>).
            </p>
          </Block>
        </div>

        <div className="mt-12 pt-10 border-t border-slate-200 dark:border-slate-800">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400 font-semibold mb-6">Pełna treść dokumentu</div>
          <div data-testid="rodo-full-text" className="max-w-3xl space-y-8">
            {FULL_RODO.map((section, i) => (
              <div key={i} data-testid={`rodo-full-section-${i + 1}`}>
                {section.h && <h4 className="font-display font-bold text-base sm:text-lg tracking-tight">{section.h}</h4>}
                <div className="mt-3 space-y-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {section.ps.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
